import React, { useState } from 'react';
import { Tooltip, Button, Pagination } from 'antd';
import { Visibility, ContactMail, AssignmentInd, EventNote } from '@material-ui/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { Participation } from 'core/worldCup/customerSupport/types';
import { PAGE_SIZE_OPTIONS } from 'utilities/constants';
import DetailModal, { DetailSection } from 'components/Basic/DetailModal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

type CustomerSupportTableType = {
  participationsData: Participation[] | null;
  total: number;
  current: number;
  pageSize: number;
  onChangePage: (page: number, size: number) => void;
  setCurrent: (e: number) => void;
  setPageSize: (e: number) => void;
  onViewDetail: (id: number) => void;
};

const CustomerSupportTable: React.FC<CustomerSupportTableType> = ({
  participationsData,
  total,
  current,
  pageSize,
  onChangePage,
  setCurrent,
  setPageSize,
  onViewDetail,
}) => {
  const intl = useIntl();
  const [selectedParticipant, setSelectedParticipant] = useState<Participation | null>(null);

  const handleChangePage = (page: number, size: number) => {
    setCurrent(page);
    setPageSize(size);
    onChangePage(page, size);
  };

  const handleShowSizeChange = (c: number, s: number) => {
    handleChangePage(1, s);
  };

  const handleOpenDetail = (item: Participation) => {
    setSelectedParticipant(item);
  };

  const handleCloseDetail = () => {
    setSelectedParticipant(null);
  };

  const buildSections = (p: Participation): DetailSection[] => [
    {
      icon: <ContactMail />,
      title: <FormattedMessage id="Contact" />,
      fields: [
        {
          label: <FormattedMessage id="Email" />,
          value: p.email || '-',
        },
        {
          label: <FormattedMessage id="Phone" />,
          value: p.phone || '-',
        },
        {
          label: <FormattedMessage id="Address" />,
          value: p.address || '-',
          fullWidth: true,
        },
      ],
    },
    {
      icon: <AssignmentInd />,
      title: <FormattedMessage id="Identity" />,
      fields: [
        {
          label: <FormattedMessage id="Identification type" />,
          value: p.participantIdType || '-',
        },
        {
          label: <FormattedMessage id="Identification number" />,
          value: p.participantIdNumber || '-',
        },
        {
          label: <FormattedMessage id="Date of birth" />,
          value: p.birthDate ? dayjs(p.birthDate).format('DD/MM/YYYY') : '-',
        },
        {
          label: <FormattedMessage id="State" />,
          value: p.state || '-',
        },
      ],
    },
    {
      icon: <EventNote />,
      title: <FormattedMessage id="Participation" />,
      fields: [
        {
          label: <FormattedMessage id="Registration channel" />,
          value: p.channel?.name || '-',
        },
        {
          label: <FormattedMessage id="Has VISA" />,
          value: (
            <span className={`visa_badge ${p.hasAmericanVisa ? 'yes' : 'no'}`}>
              <FormattedMessage id={p.hasAmericanVisa ? 'Yes' : 'No'} />
            </span>
          ),
        },
        {
          label: <FormattedMessage id="Created at" />,
          value: p.createdAt ? dayjs(p.createdAt).format('DD/MM/YYYY HH:mm') : '-',
        },
      ],
    },
  ];

  return (
    <div className="customerSupport_table_layout" id="customerSupport_table_layout">
      <div className="table_header">
        <div className="id">
          <FormattedMessage id="ID" />
        </div>
        <div className="customer_name">
          <FormattedMessage id="Full name" />
        </div>
        <div className="participation_code">
          <FormattedMessage id="Participation code" />
        </div>
        <div className="channel">
          <FormattedMessage id="Registration channel" />
        </div>
        <div className="email">
          <FormattedMessage id="Email" />
        </div>
        <div className="phone">
          <FormattedMessage id="Phone" />
        </div>
        <div className="dpi">
          <FormattedMessage id="Identification number" />
        </div>
        <div className="visa">
          <FormattedMessage id="Has VISA" />
        </div>
        <div className="actions">
          <FormattedMessage id="Actions" />
        </div>
      </div>

      <div>
        <div className="table_content" id="customerSupport_table_content">
          {Array.isArray(participationsData) &&
            participationsData.map((item, index) => (
              <div
                className="table_item"
                key={index}
                id={`participation_item_${item?.id}`}
              >
                <div className="id">
                  <p>{item?.id}</p>
                </div>
                <div className="customer_name">
                  <p>{item.fullName || '-'}</p>
                </div>
                <div className="participation_code">
                  <p>{item.participationCode || '-'}</p>
                </div>
                <div className="channel">
                  <p>{item.channel ? item.channel.name : '-'}</p>
                </div>
                <div className="email">
                  <p>{item.email || '-'}</p>
                </div>
                <div className="phone">
                  <p>{item.phone ? `${item.phone}` : '-'}</p>
                </div>
                <div className="dpi">
                  <p>{item.participantIdNumber || '-'}</p>
                </div>
                <div className="visa">
                  <span className={`visa_badge ${item.hasAmericanVisa ? 'yes' : 'no'}`}>
                    {item.hasAmericanVisa
                      ? intl.formatMessage({ id: 'Yes' })
                      : intl.formatMessage({ id: 'No' })}
                  </span>
                </div>
                <div className="actions">
                  <Tooltip
                    placement="top"
                    title={intl.formatMessage({ id: 'View details' })}
                    id="participation_view_tooltip"
                  >
                    <Button
                      className="edit_btn"
                      onClick={() => handleOpenDetail(item)}
                      id="participation_view_btn"
                    >
                      <Visibility />
                    </Button>
                  </Tooltip>
                </div>
              </div>
            ))}
        </div>

        <div className="pagination_wrapper">
          <Pagination
            showSizeChanger
            defaultCurrent={1}
            defaultPageSize={10}
            current={current}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            total={total}
            onChange={handleChangePage}
            onShowSizeChange={handleShowSizeChange}
          />
          <div className="total">Resultados: {total}</div>
        </div>
      </div>

      {selectedParticipant && (
        <DetailModal
          className="participant_detail_modal"
          visible={!!selectedParticipant}
          width={560}
          title={selectedParticipant.fullName || '-'}
          subtitle={`#${selectedParticipant.participationCode || intl.formatMessage({ id: 'No code' })}`}
          avatarLetter={selectedParticipant.fullName?.charAt(0).toUpperCase() || '?'}
          onClose={handleCloseDetail}
          sections={buildSections(selectedParticipant)}
        />
      )}
    </div>
  );
};

export default CustomerSupportTable;
